import { useCallback, KeyboardEvent } from 'react';
import clsx from 'clsx';

type SwitchSize = 'medium' | 'small' | 'large';
type SwitchClassMap = Record<SwitchSize, string>;

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: SwitchSize;
  className?: string;
  'aria-label': string;
}

function Switch({
  checked,
  onChange,
  disabled = false,
  size = 'medium',
  className,
  'aria-label': ariaLabel,
}: SwitchProps) {
  const handleClick = useCallback(() => {
    if (!disabled) {
      onChange(!checked);
    }
  }, [checked, disabled, onChange]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        if (!disabled) {
          onChange(!checked);
        }
      }
    },
    [checked, disabled, onChange],
  );

  const sizeClasses: SwitchClassMap = {
    small: 'w-6 h-3',
    medium: 'w-8 h-4',
    large: 'w-[50px] h-[31px]',
  };

  const handleSizeClasses: SwitchClassMap = {
    small: 'w-3 h-2',
    medium: 'w-4 h-3',
    large: 'w-[27px] h-[27px]',
  };

  const handleTranslateClasses: SwitchClassMap = {
    small: checked ? 'translate-x-2' : 'translate-x-0',
    medium: checked ? 'translate-x-3' : 'translate-x-0',
    large: checked ? 'translate-x-[19px]' : 'translate-x-0',
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={clsx(
        'switch relative inline-flex flex-shrink-0 rounded-full transition-colors duration-250 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-prominent)] focus-visible:ring-offset-2',
        sizeClasses[size],
        {
          'switch-active': checked && !disabled,
          'switch-default': !checked && !disabled,
          'bg-surface-secondary': disabled,
          'cursor-pointer': !disabled,
        },
        className,
      )}
    >
      <span
        className={clsx(
          'relative top-0.5 left-0.5 inline-block rounded-full bg-[var(--switch-knob)] transition-transform duration-250',
          handleSizeClasses[size],
          handleTranslateClasses[size],
          {
            'opacity-50': disabled,
          },
        )}
      />
    </button>
  );
}

export default Switch;
